import { Injectable } from '@angular/core';
import { Workbook, Worksheet } from 'exceljs';
import { saveAs } from 'file-saver';

export interface ExcelColumnDef {
  /** Property key on the data object */
  key: string;
  /** Display header in the Excel file */
  header: string;
  /** Data type hint for cell formatting */
  type?: 'string' | 'number' | 'date';
  /** Optional column width (in characters) */
  width?: number;
}

@Injectable({
  providedIn: 'root',
})
export class ExcelExportService {
  /**
   * Export data to an .xlsx file with typed cells.
   *
   * @param data        Array of record objects
   * @param columns     Column definitions (key, header, type, width)
   * @param sheetName   Worksheet tab name (default: 'Sheet1')
   *
   * @returns filename used for the download
   * @throws  Error on generation failure (caller should handle)
   */
  async exportToExcel<T extends Record<string, unknown>>(
    data: T[],
    columns: ExcelColumnDef[],
    sheetName: string = 'Migration Tracker',
  ): Promise<string> {
    const workbook = new Workbook();
    workbook.creator = 'eRM Data Migration';
    workbook.created = new Date();

    const worksheet: Worksheet = workbook.addWorksheet(sheetName);

    // Define columns
    worksheet.columns = columns.map((col) => ({
      header: col.header,
      key: col.key,
      width: col.width || this.getDefaultWidth(col),
    }));

    // Style header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, size: 11 };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF083566' },
    };
    headerRow.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
    headerRow.alignment = { vertical: 'middle', horizontal: 'left' };
    headerRow.height = 24;

    // Add data rows with typed cells
    for (const record of data) {
      const rowValues: Record<string, unknown> = {};
      for (const col of columns) {
        rowValues[col.key] = this.formatCellValue(record[col.key], col.type);
      }
      worksheet.addRow(rowValues);
    }

    // Apply date format to date columns
    for (const col of columns) {
      if (col.type === 'date') {
        const colIndex = columns.indexOf(col) + 1;
        const excelCol = worksheet.getColumn(colIndex);
        excelCol.numFmt = 'yyyy-mm-dd hh:mm:ss AM/PM';
      }
    }

    // Auto-filter on all columns
    worksheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: data.length + 1, column: columns.length },
    };

    // Freeze header row
    worksheet.views = [{ state: 'frozen', ySplit: 1 }];

    // Generate filename: Migration_Tracker_Export_YYYYMMDD_HHMM.xlsx
    const filename = this.generateFilename();

    // Write to buffer and trigger download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, filename);

    return filename;
  }

  /**
   * Generate filename: Migration_Tracker_Export_YYYYMMDD_HHMM.xlsx
   * Timestamp in user's local time.
   */
  private generateFilename(): string {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    return `Migration_Tracker_Export_${yyyy}${mm}${dd}_${hh}${min}.xlsx`;
  }

  private formatCellValue(
    value: unknown,
    type?: 'string' | 'number' | 'date',
  ): unknown {
    if (value === null || value === undefined) {
      return '';
    }

    switch (type) {
      case 'date':
        // Convert ISO string or date string to a JS Date for Excel date cell
        if (value instanceof Date) return value;
        if (typeof value === 'string' && value.trim()) {
          const parsed = new Date(value);
          return isNaN(parsed.getTime()) ? value : parsed;
        }
        return value;

      case 'number':
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
          const num = parseFloat(value.replace('%', '').trim());
          return isNaN(num) ? value : num;
        }
        return value;

      default:
        return typeof value === 'object' ? JSON.stringify(value) : String(value);
    }
  }

  private getDefaultWidth(col: ExcelColumnDef): number {
    switch (col.type) {
      case 'date':
        return 24;
      case 'number':
        return 12;
      default:
        return Math.max(col.header.length + 4, 16);
    }
  }
}
